import { useState } from 'react'

import { AdBlock } from '@hhgtech/hhg-components'
import { Button } from '@hhgtech/hhg-components/mantine'
import { Box, BoxProps, rem } from '@mantine/core'
import { useIntl } from 'react-intl'

import { Props as ArticleCardWrapperProps } from 'components/molecules/articleCardWrapper'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import {
  Article,
  ArticleDisplayType,
  Category,
  KeyPillar,
  KeyPillarsArticlesType,
} from 'interfaces/types'

import { AdvanceLayout } from './advanceLayout'
import { AdvanceLayoutWithLoadMore } from './advanceLayoutWithLoadMore'
import useStyles from './index.styles'
import { KeyPillarLayout } from './keyPillars'
import { NormalLayout } from './normalLayout'
import { ScrollAreaNav } from '../../../molecules/scrollAreaNav'
import SectionWrapper, { HeaderProps } from '../sectionWrapper'

export type ArticleCardListProps = {
  articles: Article[]
  listingByKeyPillar?: {
    listing: KeyPillarsArticlesType[]
    keyPillars?: KeyPillar[]
  }
  display?: ArticleDisplayType
  cardProps?: ArticleCardWrapperProps
  wrapperListProps?: BoxProps
  adUnitPath?: string
  readmoreArticles?: string[]
  applyH1?: boolean
  listCategories: Category[]
  wrapperProps?: HeaderProps
  mobileLayoutStretch?: boolean
} & BoxProps

const ArticleCardList = ({
  articles = [],
  listCategories,
  listingByKeyPillar,
  display = 'list-contain-ads',
  cardProps,
  wrapperListProps,
  adUnitPath,
  readmoreArticles = [],
  styles,
  applyH1,
  className,
  classNames,
  wrapperProps,
  mobileLayoutStretch,
  ...rest
}: ArticleCardListProps) => {
  const { formatMessage: f } = useIntl()
  const { classes, cx, theme } = useStyles(undefined, {
    name: 'ArticleCardList',
    styles,
    classNames,
  })
  const isMobile = useIsMobile()
  const [activeIndex, setActiveIndex] = useState<number>(-1)
  const currentUrl = useCurrentUrl()

  const dataTrackingCommon = (article: Article) => {
    if (!article) return undefined
    return {
      dataEventCategoryForCategory: 'Service Connection',
      dataEventActionForCategory: `Category Title Click`,
      // dataEventLabelForCategory: `https://${domainLocales[locale]}${article.category.permalinkDecode}`,
      dataEventCategoryForTitle: 'Service Connection',
      dataEventActionForTitle: `Article Title Click`,
      // dataEventLabelForTitle: `https://${domainLocales[locale]}${article.permalinkDecode}`,
      dataEventCategoryForBanner: 'Service Connection',
      dataEventActionForBanner: `Article Banner Click`,
      // dataEventLabelForBanner: `https://${domainLocales[locale]}${article.permalinkDecode}`,
      dataEventCategoryForReviewer: 'Service Connection',
      dataEventActionForReviewer: 'Expert Title Click',
    }
  }

  return (
    <SectionWrapper.Wrapper
      noPadding
      className={cx(className, classes.root)}
      {...rest}
    >
      {isMobile && adUnitPath && (
        <AdBlock
          wrapperProps={{
            my: '1rem',
            mx: 'auto',
          }}
          adUnitPath={adUnitPath}
          size={[
            [300, 250],
            [300, 600],
          ]}
          divId={'article-top-banner-mobile'}
          pos={['TopBanner']}
        />
      )}
      <SectionWrapper.Header
        {...wrapperProps}
        styles={{
          root: {
            ...(display === 'key-pillars' && {
              display: 'flex',
              gap: rem(24),
              [theme.fn.smallerThan('sm')]: {
                flexDirection: 'column',
              },
              [theme.fn.largerThan('sm')]: {
                justifyContent: 'space-between',
                alignItems: 'center',
              },
            }),
          },
        }}
      >
        {display === 'key-pillars' && (
          <Box
            sx={{
              [theme.fn.smallerThan('sm')]: {
                marginRight: -16,
              },
            }}
          >
            <ScrollAreaNav classNames={{ root: classes.pillarBar }}>
              <Button
                className={cx('pointer-event-child-none', classes.pillarBtn)}
                variant={activeIndex === -1 ? 'primary' : 'tertiary'}
                data-event-category="Service Connection"
                data-event-action={`Articles Key Pillar - ${f({
                  id: 'common.all',
                })}`}
                data-event-label={currentUrl}
                onClick={() => setActiveIndex(-1)}
              >
                {f({
                  id: 'common.all',
                })}
              </Button>
              {listingByKeyPillar?.keyPillars?.map((i, idx) => (
                <Button
                  className={cx('pointer-event-child-none', classes.pillarBtn)}
                  key={idx}
                  variant={activeIndex === idx ? 'primary' : 'tertiary'}
                  onClick={() => setActiveIndex(idx)}
                  data-event-category="Service Connection"
                  data-event-action={`Articles Key Pillar - ${i.name}`}
                  data-event-label={currentUrl}
                >
                  {i.name}
                </Button>
              ))}
            </ScrollAreaNav>
          </Box>
        )}
      </SectionWrapper.Header>
      {display === 'key-pillars' ? (
        <KeyPillarLayout
          categories={listCategories}
          activeIndex={activeIndex}
          listByGroup={listingByKeyPillar?.listing || []}
          dataTrackingCommon={dataTrackingCommon}
        />
      ) : display === 'advance' && readmoreArticles.length > 0 ? (
        <AdvanceLayoutWithLoadMore
          categories={listCategories}
          articles={articles}
          readmoreArticles={readmoreArticles}
          dataTrackingCommon={dataTrackingCommon}
        />
      ) : display === 'advance' ? (
        <AdvanceLayout
          categories={listCategories}
          articles={articles}
          dataTrackingCommon={dataTrackingCommon}
          mobileLayoutStretch={mobileLayoutStretch}
        />
      ) : (
        <NormalLayout
          categories={listCategories}
          articles={articles}
          dataTrackingCommon={dataTrackingCommon}
          adUnitPath={adUnitPath}
          layout={display}
        />
      )}
      {/* </Stack> */}
      {isMobile && adUnitPath && (
        <AdBlock
          wrapperProps={{
            my: '1rem',
            mx: 'auto',
          }}
          adUnitPath={adUnitPath}
          size={[
            [300, 250],
            [300, 600],
          ]}
          divId={'article-mid-banner-mobile'}
          pos={['MidBanner1']}
        />
      )}
    </SectionWrapper.Wrapper>
  )
}

export { ArticleCardList }
